import React from 'react';
import { RequirementsProps } from '../../tools/context';
import { replaceUrlParam } from '../../tools/tools';
import './Pagination.scss';

export interface PaginationProps {
  nbPages?: number;
  currentPage?: number;
  handleChangePage: (p: number) => Promise<void>;
  requirements: RequirementsProps;
}

const Pagination = ({ nbPages, currentPage, requirements, handleChangePage }: PaginationProps) => {
  if (!nbPages || nbPages <= 1) return null;

  const safeCurrentPage = currentPage ?? 0;

  const handleClientSide = (e: React.MouseEvent<HTMLAnchorElement>, page: number) => {
    e.preventDefault();

    const newUrl = replaceUrlParam('page', page, window.location.href);
    window.history.pushState({}, '', newUrl);
    handleChangePage && handleChangePage(page);

    return false;
  };

  const renderPageLink = (page: number) => (
    <a
      key={`p-${page}`}
      className="gridPagination__link"
      href={replaceUrlParam('page', page, requirements.href)}
      onClick={(e) => handleClientSide(e, page)}
    >
      {page + 1}
    </a>
  );

  const renderArrow = (key: string, page: number, label: string, text: string) => (
    <a
      key={key}
      className="gridPagination__arrow"
      href={replaceUrlParam('page', page, requirements.href)}
      onClick={(e) => handleClientSide(e, page)}
      aria-label={label}
    >
      {text}
    </a>
  );

  const showPrev = safeCurrentPage > 0;
  const showNext = safeCurrentPage < nbPages - 1;

  if (nbPages <= 5) {
    return (
      <nav className="gridPagination" aria-label="Pagination">
        {showPrev && renderArrow('first', 0, 'Première page', '«')}
        {showPrev && renderArrow('prev', safeCurrentPage - 1, 'Page précédente', '‹')}
        {new Array(nbPages).fill(null).map((_, i) =>
          i === safeCurrentPage ? (
            <span key={`p-${i}`} className="gridPagination__current" aria-current="page">
              {i + 1}
            </span>
          ) : (
            renderPageLink(i)
          )
        )}
        {showNext && renderArrow('next', safeCurrentPage + 1, 'Page suivante', '›')}
        {showNext && renderArrow('last', nbPages - 1, 'Dernière page', '»')}
      </nav>
    );
  }

  let max = Math.min(safeCurrentPage + 2, nbPages - 1);
  let min = Math.max(0, safeCurrentPage - 2);
  let nb = max - min;

  if (nb < 4 && safeCurrentPage + 2 < nbPages - 1) max += 4 - nb;
  if (nb < 4 && safeCurrentPage - 2 > 0) min -= 4 - nb;
  nb = max - min;

  return (
    <nav className="gridPagination" aria-label="Pagination">
      {showPrev && renderArrow('first', 0, 'Première page', '«')}
      {showPrev && renderArrow('prev', safeCurrentPage - 1, 'Page précédente', '‹')}
      {min > 0 && (
        <a
          className="gridPagination__link"
          href={replaceUrlParam('page', 0, requirements.href)}
          onClick={(e) => handleClientSide(e, 0)}
        >
          1
        </a>
      )}
      {min > 1 && <span className="gridPagination__ellipsis">…</span>}
      {new Array(nb + 1).fill(null).map((_, i) =>
        min + i === safeCurrentPage ? (
          <span key={`p-${min + i}`} className="gridPagination__current" aria-current="page">
            {min + i + 1}
          </span>
        ) : (
          <a
            key={`p-${min + i}`}
            className="gridPagination__link"
            href={replaceUrlParam('page', min + i, requirements.href)}
            onClick={(e) => handleClientSide(e, min + i)}
          >
            {min + i + 1}
          </a>
        )
      )}
      {max < nbPages - 2 && <span className="gridPagination__ellipsis">…</span>}
      {max < nbPages - 1 && (
        <a
          className="gridPagination__link"
          href={replaceUrlParam('page', nbPages - 1, requirements.href)}
          onClick={(e) => handleClientSide(e, nbPages - 1)}
        >
          {nbPages}
        </a>
      )}
      {showNext && renderArrow('next', safeCurrentPage + 1, 'Page suivante', '›')}
      {showNext && renderArrow('last', nbPages - 1, 'Dernière page', '»')}
    </nav>
  );
};

export default Pagination;
